import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FarmersNav from "@/components/FarmersNav";
import { CloudSun, ArrowLeft, MapPin, Wind, Drop, Sun, Moon, Thermometer } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * FarmersWeather — Weather & Farm Almanac at /farmers/weather.
 * Uses Open-Meteo (free, no key) for:
 *  - Current temp/humidity/wind/precip
 *  - 7 + 14-day forecast (temperature, precipitation, sunrise/sunset)
 *  - Historical monthly rainfall (last 12 months)
 *  - Moon phases (approximation)
 */
export default function FarmersWeather() {
  const [loc, setLoc] = useState(() => {
    try {
      const raw = localStorage.getItem("np:pulse-location");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [w, setW] = useState(null);
  const [hist, setHist] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!loc?.lat) return;
    setLoading(true);
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lon}` +
      "&current=temperature_2m,relative_humidity_2m,precipitation,rain,wind_speed_10m,wind_direction_10m,weather_code,is_day,cloud_cover,pressure_msl,uv_index" +
      "&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,uv_index_max,sunrise,sunset,wind_speed_10m_max" +
      "&hourly=relative_humidity_2m" +
      "&timezone=auto&forecast_days=14";
    fetch(url).then((r) => r.json()).then(setW).catch(() => setW(null)).finally(() => setLoading(false));

    // 12-month historical rainfall (monthly totals from ERA5 archive)
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    const hurl = `https://archive-api.open-meteo.com/v1/archive?latitude=${loc.lat}&longitude=${loc.lon}` +
      `&start_date=${iso(start)}&end_date=${iso(end)}&daily=precipitation_sum&timezone=auto`;
    fetch(hurl).then((r) => r.json()).then((d) => {
      if (!d?.daily?.time) return setHist(null);
      const totals = {};
      d.daily.time.forEach((dt, i) => {
        const m = dt.slice(0, 7);
        totals[m] = (totals[m] || 0) + (d.daily.precipitation_sum[i] || 0);
      });
      const rows = Object.entries(totals).map(([month, mm]) => ({ month, mm: Math.round(mm) }));
      rows.sort((a, b) => a.month.localeCompare(b.month));
      setHist(rows);
    }).catch(() => setHist(null));
  }, [loc?.lat, loc?.lon]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation is not supported here.");
    navigator.geolocation.getCurrentPosition((p) => {
      const l = { lat: p.coords.latitude, lon: p.coords.longitude, label: "My location" };
      setLoc(l);
      try { localStorage.setItem("np:pulse-location", JSON.stringify(l)); } catch { /* ignore */ }
    }, () => toast.error("Could not read your location."));
  };

  const maxRain = hist ? Math.max(1, ...hist.map((h) => h.mm)) : 1;

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-32" data-testid="farmers-weather-page">
      <FarmersNav />
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 mt-6">
        <Link to="/farmers" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
              <CloudSun size={12} weight="fill" /> / Weather & Farm Almanac
            </div>
            <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
              What is the sky doing?
            </h1>
            <p className="mt-2 font-serif-display italic text-[#3A3830]">
              Current conditions, 14-day forecast, rainfall history and moon phases for your location.
            </p>
          </div>
          <button
            onClick={useMyLocation}
            data-testid="farmers-weather-use-location"
            className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[color:var(--np-brand,#B85F2A)]"
          >
            <MapPin size={11} weight="fill" /> {loc ? "Update location" : "Use my location"}
          </button>
        </div>

        {!loc?.lat ? (
          <div className="mt-8 rounded-2xl border border-dashed border-black/20 bg-white/70 p-10 text-center">
            <CloudSun size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-2xl">Set your location to see local weather.</div>
          </div>
        ) : loading || !w?.current ? (
          <div className="mt-8 h-40 bg-black/5 rounded-2xl animate-pulse" />
        ) : (
          <>
            {/* Current */}
            <section className="mt-8 rounded-2xl border border-black/10 bg-white p-6" data-testid="farmers-weather-current">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat Icon={Thermometer} label="Temperature" value={`${Math.round(w.current.temperature_2m)}°C`} />
                <Stat Icon={Drop} label="Humidity" value={`${w.current.relative_humidity_2m}%`} />
                <Stat Icon={Wind} label="Wind" value={`${Math.round(w.current.wind_speed_10m)} km/h`} />
                <Stat Icon={Drop} label="Rain today" value={`${w.current.precipitation ?? 0} mm`} />
                {w.current.uv_index !== undefined && <Stat Icon={Sun} label="UV" value={w.current.uv_index} />}
                {w.current.cloud_cover !== undefined && <Stat Icon={CloudSun} label="Cloud" value={`${w.current.cloud_cover}%`} />}
                {w.current.pressure_msl !== undefined && <Stat Icon={Wind} label="Pressure" value={`${Math.round(w.current.pressure_msl)} hPa`} />}
              </div>
            </section>

            {/* 14-day forecast */}
            <section className="mt-6 rounded-2xl border border-black/10 bg-white p-6" data-testid="farmers-weather-forecast">
              <h2 className="font-serif-display text-2xl md:text-3xl">14-day forecast</h2>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                {w.daily.time.map((d, i) => (
                  <div key={d} className="rounded-lg border border-black/10 bg-[#FBF2E1]/40 p-3 text-center">
                    <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#3A3830]">
                      {new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                    </div>
                    <div className="mt-1 font-serif-display text-lg leading-tight">
                      {Math.round(w.daily.temperature_2m_min[i])}° / {Math.round(w.daily.temperature_2m_max[i])}°
                    </div>
                    <div className="text-[11px] text-[#3A3830]">{w.daily.precipitation_sum[i] ?? 0} mm</div>
                    <div className="mt-1 text-[10px] font-mono-tech text-[#3A3830]">
                      ☀ {w.daily.sunrise[i]?.slice(11, 16)} · 🌙 {w.daily.sunset[i]?.slice(11, 16)}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Rainfall history */}
            {hist && hist.length > 0 && (
              <section className="mt-6 rounded-2xl border border-black/10 bg-white p-6" data-testid="farmers-weather-history">
                <h2 className="font-serif-display text-2xl md:text-3xl">Rainfall — last 12 months</h2>
                <div className="mt-4 grid grid-cols-6 md:grid-cols-12 gap-1 items-end h-32">
                  {hist.map((h) => (
                    <div key={h.month} className="h-full flex flex-col items-center justify-end gap-1">
                      <div
                        className="w-full rounded-t bg-sky-500/70 border border-sky-600/40"
                        style={{ height: `${Math.max(4, (h.mm / maxRain) * 100)}%` }}
                        title={`${h.month}: ${h.mm} mm`}
                      />
                      <div className="font-mono-tech text-[8px] tracking-[0.2em] uppercase text-[#3A3830]">
                        {h.month.slice(5)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-[#3A3830]">Monthly totals in millimetres. Source: Open-Meteo ERA5 archive.</div>
              </section>
            )}

            {/* Moon phases */}
            <MoonPanel />
          </>
        )}
      </div>
    </main>
  );
}

const Stat = ({ Icon, label, value }) => (
  <div className="rounded-lg border border-black/10 bg-[#FBF2E1]/40 p-3">
    <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830] flex items-center gap-1">
      {Icon && <Icon size={11} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />} {label}
    </div>
    <div className="mt-1 font-serif-display text-xl">{value}</div>
  </div>
);

/**
 * MoonPanel — deterministic moon-phase approximation (Conway's method).
 * Shows the current phase + the next 4 lunar events.
 */
function MoonPanel() {
  const phases = ["New Moon", "Waxing Crescent", "First Quarter", "Waxing Gibbous", "Full Moon", "Waning Gibbous", "Last Quarter", "Waning Crescent"];
  const emojis = ["🌑", "🌒", "🌓", "🌔", "🌕", "🌖", "🌗", "🌘"];
  const jd = (d) => {
    const a = Math.floor((14 - (d.getUTCMonth() + 1)) / 12);
    const y = d.getUTCFullYear() + 4800 - a;
    const m = (d.getUTCMonth() + 1) + 12 * a - 3;
    return d.getUTCDate() + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;
  };
  const moonAge = (d) => {
    const days = jd(d) - 2451550.1;
    const phase = ((days / 29.53058867) % 1 + 1) % 1;
    return phase; // 0..1
  };
  const idx = (p) => Math.floor(p * 8 + 0.5) % 8;
  const today = new Date();
  const upcoming = Array.from({ length: 8 }).map((_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() + i * 4);
    const p = moonAge(d);
    return { d, phase: phases[idx(p)], emoji: emojis[idx(p)] };
  });
  return (
    <section className="mt-6 rounded-2xl border border-black/10 bg-white p-6" data-testid="farmers-weather-moon">
      <h2 className="font-serif-display text-2xl md:text-3xl flex items-center gap-2"><Moon size={20} weight="fill" /> Moon phases</h2>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-2">
        {upcoming.map((u, i) => (
          <div key={i} className="rounded-lg border border-black/10 bg-[#FBF2E1]/40 p-3 text-center">
            <div className="text-2xl">{u.emoji}</div>
            <div className="mt-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">
              {u.d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </div>
            <div className="mt-0.5 text-xs">{u.phase}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-xs text-[#3A3830]">Approximate — computed with Conway's phase algorithm. Useful for planting almanacs.</div>
    </section>
  );
}
